import { type RefObject, useEffect, useRef, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { usePermissions } from '@/contexts/auth';
import {
    type ProviderAccount,
    parseProviderConfigError,
    useProviderAccounts,
    useSaveProviderAccount,
} from '@/domain/providerConfig';
import { useClientErrors } from '@/hooks/useClientErrors';
import { hasErrorOfType, SERVER_ERROR } from '@/routes/shared/ProviderConfig/form/errorTypes';
import { serverErrorView } from '@/routes/shared/ProviderConfig/form/serverErrors';

import {
    type AccountFormValues,
    accountFormPath,
    buildAccountSaveInput,
    hasAccountChanges,
    toAccountFormValues,
} from './accountForm';
import { accountFormErrors, isAccountFormPath } from './accountRules';

const CHANGED_KEY = 'providerConfig.accounts.changed';
const INVALID_KEY = 'providerConfig.errors.invalid';
const GENERIC_KEY = 'providerConfig.errors.generic';

export interface AccountEditor {
    form: UseFormReturn<AccountFormValues>;
    /** The row the form shows. */
    base: ProviderAccount;
    isSaving: boolean;
    onSubmit: () => Promise<void>;
    saveDisabled: boolean;
    formMessageKey: string | null;
    /** The form element, so a refused Save can move focus to the first marked field. */
    formRef: RefObject<HTMLFormElement | null>;
}

/** One account's form, its client rules and its save; the modal only renders it. */
export function useAccountEditor(account: ProviderAccount, onClose: () => void): AccountEditor {
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = usePermissions();
    const { refetch } = useProviderAccounts({ enabled: isOptoScaleAdmin });
    const { save, isSaving, reset: forgetSaveRequest } = useSaveProviderAccount();
    // The row the form shows; a save is checked against its updatedAt, never a newer one.
    const [base, setBase] = useState(account);
    // Messages about the form as a whole; "check the marked fields" is derived instead, so it
    // goes away with the last mark.
    const [messageKey, setMessageKey] = useState<string | null>(null);
    // Set by a Save click on a form that still has problems, so they show without an edit.
    const [saveTried, setSaveTried] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    // Save is disabled once the marks show, which drops focus; it goes to the first mark instead.
    const focusProblem = useRef(false);
    const form = useForm<AccountFormValues>({
        defaultValues: toAccountFormValues(account),
        mode: 'onChange',
    });
    const values = form.watch();
    const ruleErrors = accountFormErrors(base, values);
    // As on a pen card, the rules mark fields once the form is edited, not on opening it.
    const showRules = saveTried || form.formState.isDirty;
    useClientErrors(form, showRules ? ruleErrors : [], isAccountFormPath);

    // Every render, since the marks land a render after the click that asked for them.
    useEffect(() => {
        if (!focusProblem.current) return;
        const target = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
        if (!target) return;
        focusProblem.current = false;
        target.focus();
    });

    async function reloadAfterConflict(): Promise<void> {
        const fresh = (await refetch()).find((entry) => entry.providerName === base.providerName);
        // The list always holds the account when it loads, so a miss means the refetch failed.
        if (fresh === undefined) {
            setMessageKey('providerConfig.accounts.loadFailed');
            return;
        }
        setBase(fresh);
        form.reset(toAccountFormValues(fresh));
        setMessageKey(CHANGED_KEY);
    }

    function finishSaved(): void {
        ToastInfo({
            content: t('providerConfig.accounts.saved'),
            status: 'success',
            icon: 'success',
            options: { toastId: `provider-account-saved-${base.providerName}`, autoClose: 5000 },
        });
        onClose();
    }

    async function submit(submitted: AccountFormValues): Promise<void> {
        setMessageKey(null);
        try {
            await save(base.providerName, buildAccountSaveInput(base, submitted));
        } catch (error) {
            const parsed = parseProviderConfigError(error);
            // Someone saved or created the row since it was loaded.
            if (parsed.kind === 'stale' || parsed.kind === 'alreadyConnected')
                return reloadAfterConflict();
            // The save committed and only its read failed; a retry would conflict with it.
            if (parsed.kind === 'savedUnread') {
                const fresh = await refetch();
                if (fresh.some((entry) => entry.providerName === base.providerName))
                    return finishSaved();
            }
            let marked = 0;
            if (parsed.kind === 'invalid')
                for (const entry of parsed.errors) {
                    const path = accountFormPath(entry.path);
                    if (path === null) continue;
                    // The code, never the server's text: errorKeyOf turns it into copy.
                    form.setError(path, { type: SERVER_ERROR, message: entry.code });
                    marked += 1;
                }
            // A refusal that names no field here has no marks to point at.
            if (marked === 0)
                setMessageKey(
                    parsed.kind === 'invalid'
                        ? GENERIC_KEY
                        : (serverErrorView(parsed).formMessageKey ?? GENERIC_KEY)
                );
            focusProblem.current = marked > 0;
            return;
        } finally {
            // The outcome is handled above; the cached request still holds typed secrets.
            forgetSaveRequest();
        }
        finishSaved();
    }

    const submitForm = form.handleSubmit(submit);
    function onSubmit(): Promise<void> {
        if (ruleErrors.length > 0) {
            setSaveTried(true);
            focusProblem.current = true;
            return Promise.resolve();
        }
        return submitForm();
    }

    // Against the row the form shows, so typing a value back to the stored one is no edit.
    const changed = hasAccountChanges(buildAccountSaveInput(base, values));
    const blocked = showRules && ruleErrors.length > 0;
    const serverMarks = hasErrorOfType(form.formState.errors, SERVER_ERROR);
    return {
        form,
        base,
        isSaving,
        onSubmit,
        saveDisabled: isSaving || !changed || blocked,
        formMessageKey: messageKey ?? (blocked || serverMarks ? INVALID_KEY : null),
        formRef,
    };
}
